import { useCallback, type PropsWithChildren } from "react";
import { createPortal } from "react-dom";

import { styled } from "@linaria/react";

import { useDismiss } from "../hooks/useDismiss";
import { OverlayLayer } from "../overlay/constants";
import { useOverlay } from "../overlay/useOverlay";

export interface ModalProps {
  open: boolean;
  onClose: () => void;
}

const StyledModalWrapper = styled.div<{ isOpen: boolean }>`
  position: fixed;

  inset: 0;

  opacity: ${({ isOpen }) => (isOpen ? 1 : 0)};
  visibility: ${({ isOpen }) => (isOpen ? "visible" : "hidden")};

  pointer-events: none;
`;

const ModalWrapperContent = styled.div`
  position: absolute;

  display: flex;
  align-items: center;
  justify-content: center;

  inset: 0;

  pointer-events: none;
`;

const ModalContentWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: auto;
`;

const Modal = ({ open, onClose, children }: PropsWithChildren<ModalProps>) => {
  const { zIndex, isTopmost } = useOverlay(onClose, open, { layer: OverlayLayer.MODAL });

  const handleContentClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  const dismissRef = useDismiss<HTMLDivElement>({
    onDismiss: onClose,
    enabled: open && isTopmost,
    enableClickOutside: false,
  });

  return createPortal(
    open && (
      <StyledModalWrapper isOpen={open} style={{ zIndex }}>
        <ModalWrapperContent>
          <ModalContentWrapper ref={dismissRef} onClick={handleContentClick}>
            {children}
          </ModalContentWrapper>
        </ModalWrapperContent>
      </StyledModalWrapper>
    ),
    document.body,
  );
};

export default Modal;
